// Sort comparators for the super admin tables. react-table's built-in
// `alphanumeric` only special-cases `undefined`, but every sortable column on
// these two screens is nullable, so blanks would otherwise interleave with
// real values instead of collecting at the bottom.

// Structural rather than react-table's `Row<D>`: the comparators only ever
// read `values`, and keeping them free of the table types makes them plain
// functions to test.
interface SortableRow {
  values: Record<string, unknown>
}

const isBlank = (value: unknown) =>
  value === null || value === undefined || String(value).trim() === ''

// react-table negates whatever a sortType returns when the column is
// descending. Pre-inverting the blank comparison cancels that out, so blanks
// stay at the bottom in both directions instead of flipping to the top.
const blanksLast = (blankA: boolean, blankB: boolean, desc?: boolean) => {
  if (blankA && blankB) return 0
  if (blankA) return desc ? -1 : 1
  return desc ? 1 : -1
}

export const sortTextNullsLast = (
  rowA: SortableRow,
  rowB: SortableRow,
  columnId: string,
  desc?: boolean,
) => {
  const a = rowA.values[columnId]
  const b = rowB.values[columnId]
  const blankA = isBlank(a)
  const blankB = isBlank(b)

  if (blankA || blankB) return blanksLast(blankA, blankB, desc)

  return String(a)
    .trim()
    .toLowerCase()
    .localeCompare(String(b).trim().toLowerCase())
}

// Anything that is not a finite number - null, blank, or a stray label - is
// treated the same as a blank and sinks to the bottom.
const toNumber = (value: unknown) =>
  isBlank(value) ? NaN : Number(String(value).trim())

export const sortNumericNullsLast = (
  rowA: SortableRow,
  rowB: SortableRow,
  columnId: string,
  desc?: boolean,
) => {
  const a = toNumber(rowA.values[columnId])
  const b = toNumber(rowB.values[columnId])
  const blankA = Number.isNaN(a)
  const blankB = Number.isNaN(b)

  if (blankA || blankB) return blanksLast(blankA, blankB, desc)

  return a - b
}

// Dates arrive as strings and are not all spelled the same way: a date-only
// `billing_period_start` next to a full `charge_date` timestamp, and an offset
// that is not always `Z`. Comparing parsed instants avoids depending on that.
const toInstant = (value: unknown) =>
  isBlank(value) ? NaN : Date.parse(String(value).trim())

export const sortDateNullsLast = (
  rowA: SortableRow,
  rowB: SortableRow,
  columnId: string,
  desc?: boolean,
) => {
  const a = toInstant(rowA.values[columnId])
  const b = toInstant(rowB.values[columnId])
  const blankA = Number.isNaN(a)
  const blankB = Number.isNaN(b)

  if (blankA || blankB) return blanksLast(blankA, blankB, desc)

  return a - b
}
